import {
  ContentItemStatus,
  ContentPriority,
  ContentRepresentation,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyCategory,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faAdd, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { FilterDropdownList, IFilterDropdownOption, IFilterDropdownOptionGroup } from './FilterDropdownList';
import { MultiLevelDropdownPanel } from './MultiLevelDropdownPanel';
import { getCachedTaxonomies, getCachedTaxonomyTermsByFacet } from './useCachedTaxonomyData';
import { BP_POPOVER_CLASSES } from '../../../Components/brandPresenceClasses';
import { useDismissOnOutsideClick } from '../../../Components/useDismissOnOutsideClick';

const INITIAL_VISIBLE_VALUES = 5;

/**
 * `type` arrives only with a link from an analytics figure; the menu does not offer to add it, but
 * its chip opens the type's own values like any other filter's chip opens its own.
 */
type FilterTargetType = 'taxonomy' | 'status' | 'priority' | 'locale' | 'type';

interface IFilterTarget {
  id: string;
  label: string;
  type: FilterTargetType;
  taxonomyFacet?: TaxonomyFacet;
  taxonomyKey?: string;
  category?: TaxonomyCategory | 'system';
}

export interface IInventoryFilterValue {
  targetId: string;
  targetLabel: string;
  targetType: FilterTargetType;
  valueId: string;
  valueLabel: string;
}

function slugifyTermName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

function normalizeTaxonomyTermKey(rawKey: unknown, taxonomyKey: string | undefined, termName: string | undefined): string | undefined {
  const key = String(rawKey || '').trim();
  if (key && key.includes('.') && !key.includes('::')) {
    return key;
  }

  const normalizedTaxonomyKey = String(taxonomyKey || '').trim();
  const normalizedName = String(termName || '').trim();
  if (!normalizedTaxonomyKey || !normalizedName) {
    return undefined;
  }

  const slug = slugifyTermName(normalizedName);
  if (!slug) {
    return undefined;
  }

  return `${normalizedTaxonomyKey}.${slug}`;
}

export function AddFilterDropdown({
  api,
  selectedFilters,
  onChange,
  allowedTargetTypes,
}: {
  api?: ISyncCoreApi;
  selectedFilters: IInventoryFilterValue[];
  onChange: (filters: IInventoryFilterValue[]) => void;
  allowedTargetTypes?: FilterTargetType[];
}) {
  const [open, setOpen] = React.useState(false);
  const editorRef = React.useRef<HTMLDivElement>(null);
  // The menu opens from the Add Filter button or from a filter's chip; Escape returns to that one.
  const openerRef = React.useRef<HTMLElement | null>(null);
  const [draftFilters, setDraftFilters] = React.useState<IInventoryFilterValue[]>([]);
  const [targetSearch, setTargetSearch] = React.useState('');
  const [valueSearch, setValueSearch] = React.useState('');
  const [selectedTarget, setSelectedTarget] = React.useState<IFilterTarget | undefined>(undefined);
  const [pendingTargetId, setPendingTargetId] = React.useState<string | undefined>(undefined);
  const [targets, setTargets] = React.useState<IFilterTarget[]>([]);
  const [valueOptions, setValueOptions] = React.useState<IFilterDropdownOption[]>([]);
  const [visibleValues, setVisibleValues] = React.useState(INITIAL_VISIBLE_VALUES);
  const [loadingTargets, setLoadingTargets] = React.useState(false);
  const [loadingValues, setLoadingValues] = React.useState(false);

  const groupedSelectedFilters = React.useMemo(() => {
    const byTarget = new Map<string, IInventoryFilterValue[]>();
    for (const filter of selectedFilters) {
      const existing = byTarget.get(filter.targetId) || [];
      byTarget.set(filter.targetId, [...existing, filter]);
    }

    return Array.from(byTarget.values()).map((entries) => ({
      targetId: entries[0].targetId,
      targetLabel: entries[0].targetLabel,
      values: entries.map((entry) => entry.valueLabel),
      count: entries.length,
    }));
  }, [selectedFilters]);

  const allowedTargetTypeSet = React.useMemo(
    () => (allowedTargetTypes?.length ? new Set<FilterTargetType>(allowedTargetTypes) : undefined),
    [allowedTargetTypes]
  );

  React.useEffect(() => {
    if (!open || !api) {
      return;
    }

    let active = true;
    void (async () => {
      setLoadingTargets(true);

      const taxonomies = await getCachedTaxonomies(api);
      if (!active) {
        return;
      }

      const taxonomyTargets: IFilterTarget[] = taxonomies
        .filter((taxonomy) => !!taxonomy.facet)
        .map((taxonomy) => ({
          id: `taxonomy:${taxonomy.facet as string}`,
          label: taxonomy.name,
          type: 'taxonomy',
          taxonomyFacet: taxonomy.facet as TaxonomyFacet,
          taxonomyKey: String(taxonomy.key || ''),
          category: taxonomy.category as TaxonomyCategory,
        }));

      const staticTargets: IFilterTarget[] = [
        { id: 'status', label: 'Status', type: 'status', category: 'system' },
        { id: 'priority', label: 'Priority', type: 'priority', category: 'system' },
        { id: 'type', label: 'Type', type: 'type', category: 'system' },
      ];

      const nextTargets = [...staticTargets, ...taxonomyTargets];
      setTargets(
        allowedTargetTypeSet ? nextTargets.filter((target) => allowedTargetTypeSet.has(target.type as FilterTargetType)) : nextTargets
      );
      setLoadingTargets(false);
    })();

    return () => {
      active = false;
    };
  }, [allowedTargetTypeSet, api, open]);

  React.useEffect(() => {
    if (!pendingTargetId || !targets.length) {
      return;
    }

    const target = targets.find((entry) => entry.id === pendingTargetId);
    if (target) {
      setSelectedTarget(target);
    }
    setPendingTargetId(undefined);
  }, [pendingTargetId, targets]);

  React.useEffect(() => {
    if (!open || !api || !selectedTarget) {
      return;
    }

    let active = true;
    void (async () => {
      setLoadingValues(true);

      let values: IFilterDropdownOption[] = [];
      if (selectedTarget.type === 'taxonomy' && selectedTarget.taxonomyFacet) {
        const terms = await getCachedTaxonomyTermsByFacet(api, selectedTarget.taxonomyFacet);
        if (!active) {
          return;
        }

        values = terms
          .map((term) => ({
            id: normalizeTaxonomyTermKey(term.key, selectedTarget.taxonomyKey, term.name),
            label: String(term.name || '').trim(),
          }))
          .filter((term): term is IFilterDropdownOption => !!term.id && term.label.length > 0);
      } else if (selectedTarget.type === 'status') {
        values = Object.values(ContentItemStatus)
          .filter((value) => value !== ContentItemStatus.PreviousRevision)
          .map((value) => ({
            id: value,
            label: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentItemStatus', value),
          }));
      } else if (selectedTarget.type === 'priority') {
        values = Object.values(ContentPriority).map((value) => ({
          id: value,
          label: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentPriority', value),
        }));
      } else if (selectedTarget.type === 'type') {
        values = Object.values(ContentRepresentation).map((value) => ({
          id: value,
          label: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentRepresentation', value),
        }));
      }

      setValueOptions(values);
      setLoadingValues(false);
    })();

    return () => {
      active = false;
    };
  }, [api, open, selectedTarget]);

  const filteredTargets = React.useMemo(() => {
    // The type narrows what an analytics figure linked to, so it is edited from its chip and never
    // offered as something to add.
    const offeredTargets = targets.filter((target) => target.type !== 'type');
    const needle = targetSearch.trim().toLowerCase();
    if (!needle) {
      return offeredTargets;
    }

    return offeredTargets.filter((target) => target.label.toLowerCase().includes(needle));
  }, [targetSearch, targets]);

  const groupedFilteredTargets = React.useMemo((): IFilterDropdownOptionGroup[] => {
    const byGroup = new Map<string, IFilterDropdownOptionGroup>();

    for (const target of filteredTargets) {
      const groupId = target.category || 'system';
      const label =
        groupId === 'system'
          ? 'System'
          : SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyCategory', groupId as TaxonomyCategory);

      const existing = byGroup.get(groupId);
      if (!existing) {
        byGroup.set(groupId, {
          id: groupId,
          label,
          options: [{ id: target.id, label: target.label }],
        });
        continue;
      }

      existing.options.push({ id: target.id, label: target.label });
    }

    return Array.from(byGroup.values()).sort((left, right) => {
      if (left.id === 'system') {
        return -1;
      }
      if (right.id === 'system') {
        return 1;
      }
      return left.label.localeCompare(right.label);
    });
  }, [filteredTargets]);

  const filteredValues = React.useMemo(() => {
    const needle = valueSearch.trim().toLowerCase();
    if (!needle) {
      return valueOptions;
    }

    return valueOptions.filter((value) => value.label.toLowerCase().includes(needle));
  }, [valueOptions, valueSearch]);

  const selectedValueOptions = React.useMemo(() => {
    if (!selectedTarget) {
      return [];
    }

    const selectedForTarget = draftFilters.filter((filter) => filter.targetId === selectedTarget.id);
    return selectedForTarget.map((entry) => ({ id: entry.valueId, label: entry.valueLabel }));
  }, [draftFilters, selectedTarget]);

  const selectedTargetOptions = React.useMemo(() => {
    const selectedTargetIds = new Set(selectedFilters.map((filter) => filter.targetId));
    return targets
      .filter((target) => selectedTargetIds.has(target.id))
      .map((target) => ({
        id: target.id,
        label: target.label,
        badgeCount: selectedFilters.filter((entry) => entry.targetId === target.id).length,
        interactive: true,
      }));
  }, [selectedFilters, targets]);

  const visibleFilteredValues = filteredValues.slice(0, visibleValues);
  const usePaginatedValues = selectedTarget?.type === 'taxonomy';

  const toggleValue = (option: IFilterDropdownOption) => {
    if (!selectedTarget) {
      return;
    }

    const exists = draftFilters.some((entry) => entry.targetId === selectedTarget.id && entry.valueId === option.id);
    if (exists) {
      setDraftFilters(draftFilters.filter((entry) => !(entry.targetId === selectedTarget.id && entry.valueId === option.id)));
      return;
    }

    // The list is narrowed to one type at a time, so choosing a type replaces the one before it.
    const keptFilters =
      selectedTarget.type === 'type' ? draftFilters.filter((entry) => entry.targetId !== selectedTarget.id) : draftFilters;

    setDraftFilters([
      ...keptFilters,
      {
        targetId: selectedTarget.id,
        targetLabel: selectedTarget.label,
        targetType: selectedTarget.type,
        valueId: option.id,
        valueLabel: option.label,
      },
    ]);
  };

  const openTargetEditor = (targetId?: string) => {
    setOpen(true);
    setDraftFilters(selectedFilters);
    setTargetSearch('');
    setValueSearch('');
    setVisibleValues(INITIAL_VISIBLE_VALUES);
    if (!targetId) {
      setPendingTargetId(undefined);
      setSelectedTarget(undefined);
      return;
    }

    const target = targets.find((entry) => entry.id === targetId);
    if (target) {
      setSelectedTarget(target);
      setPendingTargetId(undefined);
      return;
    }

    setSelectedTarget(undefined);
    setPendingTargetId(targetId);
  };

  const closeEditor = () => {
    setOpen(false);
    setSelectedTarget(undefined);
    setPendingTargetId(undefined);
    setTargetSearch('');
    setValueSearch('');
    setVisibleValues(INITIAL_VISIBLE_VALUES);
  };
  useDismissOnOutsideClick({ open, containerRef: editorRef, triggerRef: openerRef, onDismiss: closeEditor });

  return (
    <div className="relative flex items-center gap-2 flex-wrap">
      {groupedSelectedFilters.map((group) => (
        <button
          key={group.targetId}
          type="button"
          className="inline-flex h-8 items-center gap-2 rounded-full bg-primary/[0.07] pl-3 pr-2 text-xs text-slate-700 ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/[0.12] motion-safe:animate-bp-fade-in"
          onClick={(event) => {
            openerRef.current = event.currentTarget;
            openTargetEditor(group.targetId);
          }}
        >
          <span className="truncate max-w-[220px]">
            <span className="font-semibold">{group.targetLabel}:</span> {group.values.join(', ')}
          </span>
          <span
            role="button"
            tabIndex={0}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
            onClick={(event) => {
              event.stopPropagation();
              onChange(selectedFilters.filter((filter) => filter.targetId !== group.targetId));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                onChange(selectedFilters.filter((filter) => filter.targetId !== group.targetId));
              }
            }}
            title="Remove filter"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
          </span>
        </button>
      ))}

      <div className="relative" ref={editorRef}>
        <button
          type="button"
          className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
            open ? 'bg-primary/10 text-primary' : 'text-primary hover:bg-primary/[0.07]'
          }`}
          aria-expanded={open}
          onClick={(event) => {
            openerRef.current = event.currentTarget;
            if (open) {
              setOpen(false);
              setSelectedTarget(undefined);
              setPendingTargetId(undefined);
              return;
            }

            openTargetEditor(undefined);
          }}
        >
          <FontAwesomeIcon icon={faAdd} className="text-xs" /> Add Filter
        </button>

        {open ? (
          <div className={`absolute left-0 top-full z-20 mt-2 w-[360px] ${BP_POPOVER_CLASSES}`}>
            <MultiLevelDropdownPanel
              selectedTarget={selectedTarget ? { id: selectedTarget.id, label: selectedTarget.label } : undefined}
              targetSearch={targetSearch}
              onTargetSearchChange={setTargetSearch}
              targetSearchPlaceholder="Search filters"
              selectedTargetOptions={selectedTargetOptions}
              filteredTargetOptions={filteredTargets.map((target) => ({ id: target.id, label: target.label }))}
              groupedFilteredTargetOptions={groupedFilteredTargets}
              loadingTargets={loadingTargets}
              onSelectTarget={(option) => {
                const target = targets.find((entry) => entry.id === option.id);
                if (!target) {
                  return;
                }

                setSelectedTarget(target);
                setValueSearch('');
                setVisibleValues(INITIAL_VISIBLE_VALUES);
              }}
              valueSearch={valueSearch}
              onValueSearchChange={(value) => {
                setValueSearch(value);
                setVisibleValues(INITIAL_VISIBLE_VALUES);
              }}
              valueSearchPlaceholder="Search values"
              selectedValueOptions={selectedValueOptions}
              filteredValueOptions={usePaginatedValues ? visibleFilteredValues : filteredValues}
              loadingValues={loadingValues}
              valueSelectionMultiple={selectedTarget?.type !== 'type'}
              onToggleValue={toggleValue}
              hideSelectedValuesFromFiltered={true}
              onLoadMoreValues={usePaginatedValues ? () => setVisibleValues((value) => value + INITIAL_VISIBLE_VALUES) : undefined}
              canLoadMoreValues={usePaginatedValues && filteredValues.length > visibleValues}
              changeTargetLabel="Change filter"
              onBackToTargets={() => {
                setSelectedTarget(undefined);
                setValueSearch('');
                setVisibleValues(INITIAL_VISIBLE_VALUES);
              }}
              onCancel={closeEditor}
              onApply={() => {
                onChange(draftFilters);
                setOpen(false);
                setSelectedTarget(undefined);
                setPendingTargetId(undefined);
                setTargetSearch('');
                setValueSearch('');
                setVisibleValues(INITIAL_VISIBLE_VALUES);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
